import React from "react";

interface ProjectBasicInfoProps {
  formData: {
    topicName: string;
    managementLevel: string;
    managementLevelInput: string;
    provinceCity: string;
    researchField: string;
    specificProgram: string;
    proposedBudget: string;
    leadUnit: string;
    leadUnitInput: string;
    governingUnit: string;
    otherGoverningUnit: string;
    startMonth: string;
    startYear: string;
    endMonth: string;
    endYear: string;
    contractingMethod: string;
    topicType: string;
  };
  handleInputChange: (field: string, value: string) => void;
}

const ProjectBasicInfo: React.FC<ProjectBasicInfoProps> = ({
  formData,
  handleInputChange,
}) => {
  return (
    <div className="bg-gray-50 p-6 rounded-lg">
      {/* Top Field - Full Width */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Tên đề tài <span className="text-red-500">(*)</span>
        </label>
        <input
          type="text"
          value={formData.topicName}
          onChange={(e) => handleInputChange("topicName", e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Nhập tên đề tài..."
        />
      </div>

      {/* Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-4">
        {/* Cấp quản lý */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cấp quản lý: <span className="text-red-500">(*)</span>
          </label>
          <select
            value={formData.managementLevel}
            onChange={(e) =>
              handleInputChange("managementLevel", e.target.value)
            }
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">- Chọn cấp quản lý -</option>
            <option value="national">Cấp Nhà nước</option>
            <option value="ministerial">Cấp Bộ</option>
            <option value="provincial">Cấp Tỉnh/Thành phố</option>
            <option value="institutional">Cấp Cơ sở</option>
          </select>
        </div>

        {/* Nhập cấp quản lý */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nhập cấp quản lý
          </label>
          <select
            value={formData.managementLevelInput}
            onChange={(e) =>
              handleInputChange("managementLevelInput", e.target.value)
            }
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">- Chọn cấp quản lý -</option>
            <option value="national">Cấp Nhà nước</option>
            <option value="ministerial">Cấp Bộ</option>
            <option value="provincial">Cấp Tỉnh/Thành phố</option>
            <option value="institutional">Cấp Cơ sở</option>
          </select>
        </div>

        {/* Nhập tỉnh/ thành phố */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nhập tỉnh/ thành phố
          </label>
          <input
            type="text"
            value={formData.provinceCity}
            onChange={(e) => handleInputChange("provinceCity", e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Nhập tỉnh/ thành phố cho đề tài cấp tỉnh/ thành phố"
          />
        </div>

        {/* Lĩnh vực nghiên cứu */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Lĩnh vực nghiên cứu: <span className="text-red-500">(*)</span>
          </label>
          <select
            value={formData.researchField}
            onChange={(e) => handleInputChange("researchField", e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">- Chọn lĩnh vực nghiên cứu -</option>
            <option value="it">Công nghệ thông tin</option>
            <option value="economics">Kinh tế học</option>
            <option value="education">Giáo dục học</option>
            <option value="medicine">Y học</option>
          </select>
        </div>
      </div>

      {/* Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-4">
        {/* Dạng đề tài */}
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Dạng đề tài
          </label>
          <select
            value={formData.topicType}
            onChange={(e) => handleInputChange("topicType", e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">- Chọn dạng đề tài -</option>
            <option value="research">Nghiên cứu cơ bản</option>
            <option value="applied">Nghiên cứu ứng dụng</option>
            <option value="development">Nghiên cứu triển khai</option>
          </select>
        </div>

        {/* Nhập tên Chương trình cụ thể */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tên chương trình cụ thể
          </label>
          <input
            type="text"
            value={formData.specificProgram}
            onChange={(e) =>
              handleInputChange("specificProgram", e.target.value)
            }
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Nhập tên chương trình..."
          />
        </div>

        {/* Kinh phí đề xuất */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Kinh phí đề xuất <span className="text-red-500">(*)</span>
          </label>
          <input
            type="text"
            value={formData.proposedBudget}
            onChange={(e) =>
              handleInputChange("proposedBudget", e.target.value)
            }
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Nhập kinh phí đề xuất..."
          />
        </div>

        {/* Empty space for alignment */}
        <div></div>
      </div>

      {/* Row 3 */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-4">
        {/* Đơn vị chủ trì */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Đơn vị chủ trì: <span className="text-red-500">(*)</span>
          </label>
          <select
            value={formData.leadUnit}
            onChange={(e) => handleInputChange("leadUnit", e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">- Chọn Đơn vị chủ trì -</option>
            <option value="faculty1">Khoa Công nghệ thông tin</option>
            <option value="faculty2">Khoa Kinh tế</option>
            <option value="faculty3">Khoa Ngoại ngữ</option>
          </select>
        </div>

        {/* Nhập đơn vị chủ trì */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nhập đơn vị chủ trì <span className="text-red-500">(*)</span>
          </label>
          <input
            type="text"
            value={formData.leadUnitInput}
            onChange={(e) =>
              handleInputChange("leadUnitInput", e.target.value)
            }
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Nhập đơn vị chủ trì"
          />
        </div>

        {/* Đơn vị chủ quản */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Đơn vị chủ quản
          </label>
          <select
            value={formData.governingUnit}
            onChange={(e) =>
              handleInputChange("governingUnit", e.target.value)
            }
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">- Chọn Đơn vị chủ quản -</option>
            <option value="unit1">Phòng Khoa học Công nghệ</option>
            <option value="unit2">Phòng Đào tạo</option>
            <option value="unit3">Phòng Hợp tác Quốc tế</option>
          </select>
        </div>

        {/* Nhập đơn vị chủ quản khác */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nhập đơn vị chủ quản khác
          </label>
          <input
            type="text"
            value={formData.otherGoverningUnit}
            onChange={(e) =>
              handleInputChange("otherGoverningUnit", e.target.value)
            }
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Nhập đơn vị chủ quản khác...vv"
          />
        </div>
      </div>

      {/* Row 4 - Thời gian và Phương thức khoán chi */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-4">
        {/* Cột 1: Thời gian thực hiện */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Thời gian thực hiện <span className="text-red-500">(*)</span>
          </label>
          <select
            value={formData.startMonth}
            onChange={(e) => handleInputChange("startMonth", e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">- Chọn tháng -</option>
            <option value="1">Tháng 1</option>
            <option value="2">Tháng 2</option>
            <option value="3">Tháng 3</option>
            <option value="4">Tháng 4</option>
            <option value="5">Tháng 5</option>
            <option value="6">Tháng 6</option>
            <option value="7">Tháng 7</option>
            <option value="8">Tháng 8</option>
            <option value="9">Tháng 9</option>
            <option value="10">Tháng 10</option>
            <option value="11">Tháng 11</option>
            <option value="12">Tháng 12</option>
          </select>
        </div>

        {/* Cột 2: Năm */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Năm <span className="text-red-500">(*)</span>
          </label>
          <select
            value={formData.startYear}
            onChange={(e) => handleInputChange("startYear", e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">- Chọn năm -</option>
            <option value="2024">2024</option>
            <option value="2025">2025</option>
            <option value="2026">2026</option>
            <option value="2027">2027</option>
          </select>
        </div>

        {/* Cột 3: Đến tháng và Năm dự kiến - 2 input ngang hàng */}
        <div>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Đến tháng <span className="text-red-500">(*)</span>
            </label>
            <label className="block text-sm font-medium text-gray-700">
              Năm dự kiến <span className="text-red-500">(*)</span>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select
              value={formData.endMonth}
              onChange={(e) => handleInputChange("endMonth", e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">- Chọn tháng -</option>
              <option value="1">Tháng 1</option>
              <option value="2">Tháng 2</option>
              <option value="3">Tháng 3</option>
              <option value="4">Tháng 4</option>
              <option value="5">Tháng 5</option>
              <option value="6">Tháng 6</option>
              <option value="7">Tháng 7</option>
              <option value="8">Tháng 8</option>
              <option value="9">Tháng 9</option>
              <option value="10">Tháng 10</option>
              <option value="11">Tháng 11</option>
              <option value="12">Tháng 12</option>
            </select>

            <select
              value={formData.endYear}
              onChange={(e) => handleInputChange("endYear", e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">- Chọn năm -</option>
              <option value="2024">2024</option>
              <option value="2025">2025</option>
              <option value="2026">2026</option>
              <option value="2027">2027</option>
            </select>
          </div>
        </div>

        {/* Cột 4: Phương thức khoán chi */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phương thức khoán chi
          </label>
          <select
            value={formData.contractingMethod}
            onChange={(e) =>
              handleInputChange("contractingMethod", e.target.value)
            }
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">- Phương thức khoán chi -</option>
            <option value="method1">Khoán chi theo gói</option>
            <option value="method2">Khoán chi theo thời gian</option>
            <option value="method3">Khoán chi theo sản phẩm</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default ProjectBasicInfo;
